"use client";

import { solutionsPro } from "@/lib/data";
import { useUI } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { TrendingUp, Users, ArrowRight, Check } from "lucide-react";

const stats = [
  { value: "500+", label: "Entreprises" },
  { value: "99.99%", label: "Uptime Data Center" },
  { value: "120+", label: "Experts Certifiés" },
  { value: "15+", label: "Pays Desservis" },
];

export function SolutionsPro() {
  const openDevis = useUI((s) => s.openDevis);

  return (
    <section id="solutions-pro" className="py-16 sm:py-20 bg-gradient-to-b from-[#F5FBF7] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-[0.3em] text-[#006B3F] uppercase mb-3">
            Solutions Professionnelles Innovantes
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
            Solutions{" "}
            <span className="bg-gradient-to-r from-[#006B3F] to-[#008751] bg-clip-text text-transparent">
              Pro
            </span>
          </h2>
          <div className="senegal-bar mx-auto mt-5" />
          <p className="mt-4 text-gray-500 leading-relaxed">
            Du Pack Pro Complet (PC + Imprimante + Routeur 5G + Installation clé en main) à
            l&apos;IA Générative. Les solutions les plus en vogue.
          </p>
        </div>

        {/* Solution vedette */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-[#04140D] p-8 sm:p-10 mb-6"
        >
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(ellipse 50% 60% at 85% 20%, rgba(0,107,63,0.5), transparent), radial-gradient(ellipse 40% 50% at 10% 90%, rgba(255,215,0,0.12), transparent)",
            }}
          />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-xs font-black tracking-wider mb-4">
                <TrendingUp className="w-3.5 h-3.5" /> Pack #1 au Sénégal
              </span>
              <h3 className="text-2xl sm:text-3xl font-black text-white">
                Solution Pro Complète
              </h3>
              <p className="text-[#FFD700] font-semibold mt-1">PC + Imprimante + Routeur 5G</p>
              <p className="text-white/60 mt-4 leading-relaxed text-sm sm:text-base">
                Pack clé en main : PC portable pro HP/Dell, imprimante LaserJet, routeur 5G
                WiFi 7 et installation complète par nos techniciens. Support dédié 24/7 inclus.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["PC Portable Pro", "Imprimante LaserJet", "Routeur 5G WiFi 7", "Installation Clé en Main"].map((f) => (
                  <span
                    key={f}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/[0.07] border border-white/10 text-white/85"
                  >
                    <Check className="w-3 h-3 text-[#FFD700]" /> {f}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  onClick={openDevis}
                  className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold hover:shadow-[0_0_25px_rgba(255,215,0,0.4)]"
                >
                  Devis Gratuit
                </Button>
                <Button
                  variant="outline"
                  onClick={openDevis}
                  className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
                >
                  Démo
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="p-5 rounded-2xl bg-white/[0.06] border border-[#FFD700]/15 text-center backdrop-blur-sm"
                >
                  <p className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">
                    {s.value}
                  </p>
                  <p className="text-white/50 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Autres solutions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {solutionsPro.slice(1).map((sol, idx) => (
            <motion.div
              key={sol.id}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (idx % 3) * 0.08 }}
              className="group p-6 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-2xl hover:border-[#006B3F]/30 transition-all duration-500 hover:-translate-y-1"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#006B3F]/8 text-[#006B3F] text-[11px] font-bold">
                  <TrendingUp className="w-3 h-3" /> {sol.badge}
                </span>
                <Users className="w-4 h-4 text-gray-300 group-hover:text-[#006B3F] transition-colors" />
              </div>
              <h3 className="text-lg font-black text-gray-900">{sol.name}</h3>
              <p className="text-xs font-semibold text-[#006B3F] mt-0.5">{sol.tagline}</p>
              <p className="text-sm text-gray-500 mt-3 leading-relaxed">{sol.description}</p>
              <div className="mt-4 grid grid-cols-2 gap-1.5">
                {sol.features.map((f) => (
                  <span
                    key={f}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-600"
                  >
                    <Check className="w-3 h-3 text-[#008751] shrink-0" /> {f}
                  </span>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Clients</p>
                  <p className="text-xs font-semibold text-gray-700">{sol.clients}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openDevis}
                  className="border-[#006B3F]/30 text-[#006B3F] hover:bg-[#006B3F] hover:text-white"
                >
                  Devis <ArrowRight className="w-3 h-3" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
